import { screenAsset } from './screen-asset.js';
import { deviceModelManifest } from './device-model-manifest.js';
import { deviceImageDownloads } from './device-image-downloads.js';

// Lightweight network-only work can overlap the splash without loading Three,
// decoding images, building geometry, or competing with its animation on CPU.
const downloads = new Map();
const imageRequests = new Set();
const replacementScreens = {
  'iphone-crm.glb': '/screens/crm-mobile-light.webp',
  'iphone-postal.glb': '/screens/postal-mobile-light.webp',
  'iphone-hawalla.glb': '/screens/hawalla-mobile-light.webp',
  'macbook-crm.glb': '/screens/crm-dashboard-light.optimized.png',
  'macbook-hawalla.glb': '/screens/hawalla-dashboard-light.optimized.png',
};
function appearanceFor(file, appearance) {
  const replacement = replacementScreens[file];
  if (!replacement || !appearance.images?.length) return appearance;
  // Exported phones have one image; laptops have body then display images.
  return {...appearance, images: appearance.images.map((image,index) => index === appearance.images.length-1
    ? {...image, uri: screenAsset(replacement), mimeType: replacement.endsWith('.webp') ? 'image/webp' : 'image/png'} : image)};
}
function prefetchImages(appearance) {
  for (const image of appearance.images) {
    if (imageRequests.has(image.uri)) continue;
    imageRequests.add(image.uri);
    deviceImageDownloads.prefetch(image.uri);
  }
  return appearance;
}
export function downloadDeviceResource(url, type = 'arrayBuffer') {
  const key = `${type}:${url}`;
  if (!downloads.has(key)) {
    const promise = fetch(url, { priority: 'low' }).then(response => {
      if (!response.ok) throw new Error(`Device request failed (${response.status}): ${url}`);
      return response[type]();
    }).catch(error => { downloads.delete(key); throw error; });
    downloads.set(key, promise);
  }
  return downloads.get(key);
}

function preparedTemplate(descriptor) {
  return Boolean(descriptor.iosTemplate && typeof window !== 'undefined' && typeof DecompressionStream !== 'undefined');
}

function downloadBody(descriptor) {
  if (!preparedTemplate(descriptor)) return downloadDeviceResource(descriptor.template).then(body => ({body, preparedBody:false}));
  const optimized = descriptor.optimizedTemplate;
  return downloadDeviceResource(optimized ?? descriptor.iosTemplate)
    .then(body => ({body, preparedBody:true, optimizedBody:Boolean(optimized)}))
    .catch(error => {
      if (!optimized) throw error;
      // A partly updated deployment can still show its original full-detail
      // body instead of hiding the laptop when the new asset is unavailable.
      return downloadDeviceResource(descriptor.iosTemplate).then(body => ({body, preparedBody:true, optimizedBody:false}));
    });
}

export function downloadDeviceAsset(url) {
  const file = url.split('?')[0].split('/').pop(), descriptor = deviceModelManifest[file];
  if (!descriptor) return Promise.reject(new Error(`Unknown device: ${file}`));
  return Promise.all([
    downloadBody(descriptor),
    downloadDeviceResource(descriptor.appearance, 'json').then(appearance => prefetchImages(appearanceFor(file, appearance))),
  ]).then(([body, appearance]) => ({ file, descriptor, ...body, appearance }));
}

/** @param {boolean | string[]} [all] */
export function preloadDeviceAssets(all = false) {
  // Download in journey order: the App Development phone comes before Products.
  // All later appearances still share the same two compact bodies.
  for (const file of Array.isArray(all) ? all : all ? Object.keys(deviceModelManifest) : ['iphone-reference.glb', 'macbook-web.glb', 'macbook-crm.glb', 'macbook-postal.glb', 'iphone-postal.glb']) {
    const descriptor = deviceModelManifest[file];
    downloadBody(descriptor).catch(() => {});
    // Do not wait for the larger body before requesting the original screen.
    downloadDeviceResource(descriptor.appearance, 'json').then(appearance => prefetchImages(appearanceFor(file, appearance))).catch(() => {});
  }
}
